import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from './api/index.js';
import { authStore } from './auth-store.js';

/**
 * One query client for the app.
 *
 * Retrying a 4xx is pointless and, on a 401, actively harmful: it multiplies
 * the failed requests before the refresh flow can run. Only 5xx and network
 * faults are retried.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleSessionExpiry }),
  mutationCache: new MutationCache({ onError: handleSessionExpiry }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * ui-contract.md section 6 (SH-25): a session that ends mid-work answers any
 * request with UNAUTHENTICATED. Clearing the store lets `/_app` send the
 * employee to sign in again and back to this page instead of showing the error
 * inside a form. Other 401 codes (a wrong password, a locked account) are
 * shown in place.
 */
function handleSessionExpiry(error: unknown): void {
  if (!(error instanceof ApiError) || error.code !== 'UNAUTHENTICATED') return;
  if (authStore.isAuthenticated()) authStore.clear();
}
